import { ReactElement } from 'react';
import { getPriorityColor, getclasses } from '../_helpers/dashboardHelper';
import {
  DataWidgetMinMaxAttribute,
  DeviceType,
  ParameterAttribute,
  ParametersType,
  ParticleData,
  PriorityList
} from '../constants/telosair-interfaces';
import { useData } from '../context/data.context';
import { getDisplayableValue } from '../_helpers/globalHelper';
import { ParameterTooltips } from '../constants/global-constants';

type ComfortCardComponentProps = {
  comfortCardProperties: ParameterAttribute;
  particleData: ParticleData;
  minMaxData: DataWidgetMinMaxAttribute | null;
  isParameterDataLoading: boolean;
  onClick: (parameter: ParametersType, serialNumber: number) => void;
  type: DeviceType;
};

/**
 * ComfortCard component for displaying comfort factor information.
 * @param props - The props for the ComfortCard component.
 * @returns  A React element representing the ComfortCard component.
 */
export default function ComFortCard(props: ComfortCardComponentProps): ReactElement {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { clientThresholdSettings, userConfigDataV2 } = useData();
  const { comfortCardProperties, isParameterDataLoading, particleData, onClick, minMaxData, type } =
    props;
  const comfortValue = getDisplayableValue(
    comfortCardProperties.parameterName,
    particleData[comfortCardProperties.parameterName]
  );
  const priority = getPriorityColor(
    comfortCardProperties.parameterName,
    particleData[comfortCardProperties.parameterName],
    clientThresholdSettings,
    userConfigDataV2,
    type
  );

  const priorityForMax =
    minMaxData?.max &&
    Object.prototype.hasOwnProperty.call(minMaxData?.max, comfortCardProperties.parameterName)
      ? getPriorityColor(
          comfortCardProperties.parameterName,
          minMaxData?.max[comfortCardProperties.parameterName],
          clientThresholdSettings,
          userConfigDataV2,
          type
        )
      : PriorityList.none;
  const priorityForMin =
    minMaxData?.min &&
    Object.prototype.hasOwnProperty.call(minMaxData?.min, comfortCardProperties.parameterName)
      ? getPriorityColor(
          comfortCardProperties.parameterName,
          minMaxData?.min[comfortCardProperties.parameterName],
          clientThresholdSettings,
          userConfigDataV2,
          type
        )
      : PriorityList.none;

  return (
    <button
      disabled
      onClick={() => onClick(comfortCardProperties.parameterName, 1)}
      type="button"
      className="flex flex-col flex-1 justify-around items-center text-center p-2 rounded bg-background-700 grow border border-gray-800"
    >
      <div className="flex justify-center items-center">
        <p className="text-xs sm:text-sm font-medium text-gray-200">
          {comfortCardProperties.shortName}
        </p>
        <span className="ml-1 text-xs text-gray-400">
          {(userConfigDataV2?.config?.unit &&
            userConfigDataV2?.config?.unit[comfortCardProperties.parameterName]) ||
            ''}
        </span>
        <div className="relative group/tooltip ml-1">
          <span className="material-icons-outlined cursor-pointer text-base leading-4 text-gray-400">
            info
          </span>
          <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 z-50 w-60 p-2 text-xs text-white bg-gray-900 rounded-md transition-all duration-100 scale-0 origin-top group-hover/tooltip:scale-100">
            <p className="text-start">{ParameterTooltips[comfortCardProperties.parameterName]}</p>
          </div>
        </div>
        <span className="ml-1 text-xs text-gray-400">{comfortCardProperties.unit}</span>
      </div>
      <div className="flex justify-center items-center py-3">
        <p className={`text-5xl font-semibold ${getclasses(true, priority)}`}>{comfortValue}</p>
      </div>
      <div className="flex justify-center font-semibold items-center text-xs py-1 text-gray-400 rounded">
        <div className={`flex items-center px-1 `}>
          H:{' '}
          <span className={`${getclasses(true, priorityForMax)} ml-1`}>
            {isParameterDataLoading || !minMaxData?.max
              ? '-'
              : getDisplayableValue(
                  comfortCardProperties.parameterName,
                  minMaxData?.max[comfortCardProperties.parameterName]
                )}
          </span>
        </div>
        <div className={`flex items-center px-1 `}>
          L:{' '}
          <span className={`${getclasses(true, priorityForMin)} ml-1`}>
            {isParameterDataLoading || !minMaxData?.min
              ? '-'
              : getDisplayableValue(
                  comfortCardProperties.parameterName,
                  minMaxData?.min[comfortCardProperties.parameterName]
                )}
          </span>
        </div>
      </div>
      {/* <p className="text-xs text-gray-400 font-medium dark:text-gray-400">Outdoor : -</p> */}
    </button>
  );
}
